import React, { useState, useEffect, useCallback } from 'react';
import { functions } from '../../services/firebase'; // Adjust the path as necessary
import { httpsCallable } from 'firebase/functions';
import ReportTypeSelectionDialog from '../ReportTypeSelectionDialog/ReportTypeSelectionDialog'; // Import the dialog
import { reportConfigs } from '../../reports/reportConfigs';
import { saveReport } from '../../services/firestore';
import { Timestamp } from 'firebase/firestore';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog/DeleteConfirmationDialog';
import { Box, Typography, Paper, Button, CircularProgress, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'; // Added Delete icon
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  collectionGroup,
  writeBatch,
  doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';

function Home() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false); // State for dialog visibility
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  /**
 * Patch all docs that do NOT have reportType.
 * Runs once; call it from fetchReports.
 * 
 * note: this can be removed once all the reports have been updated (it needs to run for each 'user')
 */
const patchMissingReportTypes = async (snap) => {
  const batch = writeBatch(db);
  let needsCommit = false;

  snap.forEach(d => {
    if (!d.get('reportType') || d.get('reportType') == 'Economic Damages') {
      batch.update(doc(db, 'reports', d.id), { reportType: 'economic-damages' });
      needsCommit = true;
    }
  });

  if (needsCommit) await batch.commit();
};


  const fetchReports = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      setError("User not logged in.");
      return;
    }

    setLoading(true);
    setError('');
    try {
      const reportsQuery = query(
        collectionGroup(db, "reports"),
        where("userId", "==", currentUser.uid),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(reportsQuery);

      await patchMissingReportTypes(querySnapshot);

      const fetchedReports = [];
      querySnapshot.forEach((doc) => {
        fetchedReports.push({ id: doc.id, ...doc.data() });
      });
      setReports(fetchedReports);
    } catch (err) {
      console.error("Error fetching reports:", err);
      setError("Failed to load reports. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Redirect to create-report if no reports exist after loading
  // useEffect(() => {
  //   if (!loading && !error && reports.length === 0) {
  //     // navigate('/create-report', { replace: true }); // Original navigation
  //     setDialogOpen(true); // Open dialog instead
  //   }
  // }, [loading, error, reports, navigate]);
  // Commenting out the above as the "Create New Report" button will now handle this.
  // The original logic might be confusing if it auto-opens the dialog when no reports exist.
  // The user explicitly clicks "Create New Report" to trigger the dialog.

  const handleOpenDialog = async () => {
    const enabledReports = Object.values(reportConfigs).filter(config => config.enabled);

    if (enabledReports.length === 1) {
      const reportConfig = enabledReports[0];
      const initialData = {
        ...reportConfig.defaultValues(),
        reportType: reportConfig.id,
        userId: currentUser.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        status: 'in-progress',
        stepStatuses: {},
      };

      try {
        const reportId = await saveReport(initialData);
        const firstStepId = reportConfig.steps[0].id;
        navigate(`/${reportConfig.id}/${reportId}/${firstStepId}`);
      } catch (error) {
        console.error("Failed to create a new report:", error);
        // Handle error, maybe show a message to the user
      }
    } else {
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSelectReportType = (reportType) => {
    setDialogOpen(false);
    if (reportType === 'Economic Damages') {
      navigate('/create-report', { state: { reportType: 'Economic Damages' } });
    } else if (reportType === 'Life Care') {
      navigate('/life-care-plan');
    }
  };

  const handleOpenDeleteDialog = (reportId) => {
    setReportToDelete(reportId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setReportToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;

    try {
      if (!currentUser) {
        throw new Error("You must be logged in to delete reports.");
      }

      const deleteReport = httpsCallable(functions, 'deleteReport');
      const result = await deleteReport({ reportId: String(reportToDelete) });

      if (result.data.success) {
        setReports(prevReports => prevReports.filter(report => report.id !== reportToDelete));
      } else {
        throw new Error(result.data.error || "An unknown error occurred.");
      }
    } catch (error) {
      console.error("Error deleting report:", error);
      throw error; // Re-throw to be caught by the dialog
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Reports
      </Typography>
      {loading && <CircularProgress />}
      {error && <Typography color="error">{error}</Typography>}
      {!loading && !error && (
        <Paper elevation={1}>
          {reports.length > 0 ? (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Claimant Name</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Report Type</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow
                      key={report.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {/* Fallback logic for claimant name */}
                        {report.claimantName || report?.fullName || 'N/A'}
                      </TableCell>
                      <TableCell>{report.createdAt?.toDate().toLocaleDateString() || 'Unknown Date'}</TableCell>
                      <TableCell>{report.reportType || 'N/A'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton
                            edge="end"
                            aria-label="view"
                            title="View Report"
                            onClick={() => navigate(`/view-report/${report.id}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="edit"
                            title="Edit Report"
                            onClick={() => {
                              console.log(`navigating to `, report);
                              navigate(`/${report.reportType}/${report.id}/claimant-information`);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            title="Delete Report"
                            onClick={() => handleOpenDeleteDialog(report.id)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ p: 2 }}>No reports found. Create one!</Typography>
          )}
        </Paper>
      )}
       <Button
        variant="contained"
        onClick={handleOpenDialog} // Open dialog instead of direct navigation
        sx={{ mt: 3 }}
      >
        Create New Report
      </Button>
      <ReportTypeSelectionDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSelectReportType={handleSelectReportType}
      />
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Report"
        description="Are you sure you want to delete this report? This action cannot be undone."
      />
    </Box>
  );
}

export default Home;
